import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { ApolloProvider } from 'react-apollo';
import { HelmetProvider } from 'react-helmet-async';
import queryString from 'query-string';
import { initStore } from './store';
import RedirectHandler from './components/redirectHandler';
import { client } from './shared/graphql';
import './index.less';
import {
  FeatureSwitchEnhancer,
  FeatureToggle,
  useIdentifyUser,
} from './featureSwitches';
import * as Logger from '@crimson-education/browser-logger';
import { isProduction, isStaging } from './utils/settings';

const App = (props: any) => {
  const { onMessage } = props;
  const history = useHistory();
  const location = useLocation();
  const store = initStore({});
  const params = queryString.parse(location.search);
  // equivalents via the /thread/<id> shorthand
  const threadParam = params.thread || params.t;
  // If the server passes an initial redux state use that, otherwise construct our own
  if (threadParam) {
    if (params.m) {
      history.replace(`/thread/${threadParam}?m=${params.m}`);
    } else {
      history.replace(`/thread/${threadParam}`);
    }
  }
  // Make sure the page opened in the iframe
  if (window != window.top && props.registerEventEmitter) {
    props.registerEventEmitter('refresh', (url: string) => {
      history.replace(url.replace('/community', '') || '/');
    });
  }
  useEffect(() => {
    if (onMessage) {
      const url = location.pathname + location.search;
      onMessage({ event: 'route', data: url });
    }
  }, [location.pathname, location.search]);
  const identifyUser = useIdentifyUser();
  useEffect(() => {
    if (window.self === window.top) {
      const parentDomain = isProduction()
        ? 'app.crimsoneducation.org/community'
        : isStaging()
        ? 'staging.app.crimsoneducation.org/community'
        : 'localhost:3000/community';
      window.location.href = `${window.location.protocol}//${parentDomain}${window.location.pathname}`;
    }

    if (window.xprops?.userId) {
      const contextMetadata = {
        isAnonymous: 'false',
        userId: window.xprops?.userId,
      };
      try {
        Logger.addMetadata(contextMetadata);
        Logger.trackEvent({ message: 'community-app-ready' });
      } catch (e) {
        console.warn('Logger set user error', e);
      }
      identifyUser(window?.xprops?.userId);
    }
  }, [identifyUser, window?.xprops?.userId]);
  return (
    <Provider store={store}>
      <HelmetProvider>
        <ApolloProvider client={client}>
          <FeatureToggle />
          <RedirectHandler
            maintenanceMode={
              process.env.REACT_APP_MAINTENANCE_MODE === 'enabled'
            }
          />
        </ApolloProvider>
      </HelmetProvider>
    </Provider>
  );
};

type TypeEnhancer = <P extends JSX.IntrinsicAttributes>(
  Comp: React.ComponentType<P>
) => React.ComponentType<P>;
export default (FeatureSwitchEnhancer as TypeEnhancer)(App);
